import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  ArrowLeft,
  Check,
  ChevronRight,
  CircleAlert,
  Cloud,
  Database,
  Download,
  Film,
  FolderUp,
  HardDrive,
  Home,
  Info,
  Layers3,
  ListVideo,
  Menu,
  Moon,
  MoreHorizontal,
  Network,
  Plus,
  Radar,
  RefreshCw,
  Search,
  Server,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Sun,
  UploadCloud,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import type {
  AppSettings,
  FavoriteItem,
  GroupDetail,
  MetadataDetail,
  MetadataSearchItem,
  OfficialEpisode,
  SearchResult,
  SourceConfig,
} from "./types";
import {
  discoverMetadata,
  getMetadataDetails,
  getLocalIptvSources,
  getSeasonEpisodes,
  getTop,
  searchMetadata,
} from "./lib/api";
import {
  defaultSettings,
  exportLocalConfig,
  loadFavorites,
  loadSettings,
  loadSources,
  loadSourcesAsync,
  loadXtreamSession,
  saveFavorites,
  saveSettings,
  saveSources,
  saveXtreamSession,
} from "./lib/storage";
import { normalize } from "./lib/normalize";
import { useCatalogWorker } from "./hooks/useCatalogWorker";

type Page = "home" | "search" | "favorites" | "sources" | "series" | "settings";
type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};
type TrendingItem = MetadataSearchItem & {
  availableServers?: string[];
  localCount?: number;
  availabilityRevision?: number;
};

const PAGES: Page[] = ["home", "search", "sources", "series", "settings"];

function pageFromLocation(): Page {
  const statePage = history.state?.page;
  if (PAGES.includes(statePage)) return statePage;
  const hash = location.hash.slice(1);
  return PAGES.find((page) => hash === page || hash.startsWith(`${page}/`)) || "home";
}

const GENRE_PRESETS = [
  { id: "family", label: "Família", query: "melhores para família" },
  { id: "animation", label: "Animação", query: "animação" },
  { id: "comedy", label: "Comédia", query: "comédia" },
  { id: "action", label: "Ação", query: "ação" },
  { id: "scifi", label: "Ficção científica", query: "ficção científica" },
  { id: "documentary", label: "Documentários", query: "documentários" },
];

const NAV = [
  { id: "home" as Page, label: "Início", icon: Home },
  { id: "search" as Page, label: "Buscar", icon: Search },
  { id: "series" as Page, label: "Séries", icon: ListVideo },
  { id: "favorites" as Page, label: "Favoritos", icon: Star },
  { id: "sources" as Page, label: "Fontes", icon: Server },
  { id: "settings" as Page, label: "Ajustes", icon: Settings },
];

function newId() {
  return crypto.randomUUID();
}
function formatNumber(value = 0) {
  return value.toLocaleString("pt-BR");
}
function dedupeMetadata(items: MetadataSearchItem[]): TrendingItem[] {
  const seen = new Set<string>();
  return items.filter((item): item is TrendingItem => {
    const key = `${item.mediaType}-${item.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return Boolean(item.title);
  });
}
function posterFallback(kind: string) {
  return kind === "series" ? "Série" : kind === "movie" ? "Filme" : "Canal";
}
function classificationClass(value?: string) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  const age = normalized.match(/\b(10|12|14|16|18)\b/);
  if (age) return `rating-${age[1]}`;
  if (/^(l|livre|g|tv-g|tv-y)$/.test(normalized)) return "rating-livre";
  if (/^(pg|tv-y7)$/.test(normalized)) return "rating-10";
  if (/^pg-?13$/.test(normalized)) return "rating-12";
  if (/^tv-?14$/.test(normalized)) return "rating-14";
  if (/^r$/.test(normalized)) return "rating-16";
  if (/^(nc-?17|tv-?ma)$/.test(normalized)) return "rating-18";
  return `rating-${normalized.replace(/[^a-z0-9]+/g, "-")}`;
}
function safeHost(value?: string) {
  try {
    return value ? new URL(value).host : "";
  } catch {
    return value || "";
  }
}
function imageSrc(value?: string) {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.origin === location.origin) return value;
    return `/api/proxy/image?url=${encodeURIComponent(value)}`;
  } catch {
    return value.startsWith("/") ? value : undefined;
  }
}

function favoriteKeyFromDetail(detail: Pick<GroupDetail, "groupKey">) {
  return detail.groupKey;
}

function favoriteKeyFromMetadata(item: Pick<MetadataSearchItem, "id" | "mediaType">) {
  return `metadata|${item.mediaType}|${item.id}`;
}

function favoriteFromDetail(
  detail: GroupDetail,
  metadata?: MetadataDetail | null,
): FavoriteItem {
  const title = metadata?.title || detail.title;
  return {
    favoriteKey: favoriteKeyFromDetail(detail),
    groupKey: detail.groupKey,
    kind: detail.kind,
    title,
    year: metadata?.year || detail.year,
    logo: metadata?.posterUrl || detail.logo,
    servers: detail.servers,
    categories: detail.categories,
    source: detail.groupKey.startsWith("metadata|") ? "metadata" : "local",
    mediaType: metadata?.mediaType,
    metadataId: metadata?.id,
    overview: metadata?.overview,
    originalTitle: metadata?.originalTitle,
    addedAt: new Date().toISOString(),
  };
}

function favoriteFromMetadata(item: MetadataSearchItem): FavoriteItem {
  return {
    favoriteKey: favoriteKeyFromMetadata(item),
    groupKey: favoriteKeyFromMetadata(item),
    kind: item.mediaType === "tv" ? "series" : "movie",
    title: item.title,
    year: item.year,
    logo: item.posterUrl,
    servers: [],
    categories: ["Pesquisa online"],
    source: "metadata",
    mediaType: item.mediaType,
    metadataId: item.id,
    overview: item.overview,
    originalTitle: item.originalTitle,
    addedAt: new Date().toISOString(),
  };
}

function favoriteFromSearchResult(result: SearchResult): FavoriteItem {
  return {
    favoriteKey: result.groupKey,
    groupKey: result.groupKey,
    kind: result.kind,
    title: result.title,
    year: result.year,
    logo: result.logo,
    servers: result.servers,
    categories: result.categories,
    source: "local",
    addedAt: new Date().toISOString(),
  };
}

function PosterImage({
  src,
  alt,
  className,
  fallback,
}: {
  src?: string;
  alt: string;
  className?: string;
  fallback?: ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  const safeSrc = failed ? undefined : imageSrc(src);
  if (!safeSrc) return fallback || null;
  return (
    <img
      className={className}
      src={safeSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function FavoriteMark({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span className="favorite-mark" aria-hidden="true">
      <Star fill="currentColor" />
    </span>
  );
}

function useLongPressFavorite(onLongPress: () => void) {
  const timer = useRef<number | null>(null);
  const longPressed = useRef(false);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback(() => {
    longPressed.current = false;
    clear();
    timer.current = window.setTimeout(() => {
      longPressed.current = true;
      onLongPress();
    }, 520);
  }, [clear, onLongPress]);

  useEffect(() => clear, [clear]);

  const consumeLongPressClick = useCallback(() => {
    if (!longPressed.current) return false;
    longPressed.current = false;
    return true;
  }, []);

  return {
    longPressHandlers: {
      onPointerDown: start,
      onPointerUp: clear,
      onPointerLeave: clear,
      onPointerCancel: clear,
    },
    consumeLongPressClick,
  };
}

export default function App() {
  const [page, setPage] = useState<Page>(pageFromLocation);
  const [sources, setSources] = useState<SourceConfig[]>(loadSources);
  const [sourcesHydrated, setSourcesHydrated] = useState(false);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let loaded = await loadSourcesAsync();
        if (["localhost", "127.0.0.1", "::1"].includes(location.hostname)) {
          const presets = await getLocalIptvSources().catch(() => []);
          for (const preset of presets) {
            if (preset.password) saveXtreamSession(preset.source.id, preset.password);
          }
          loaded = [
            ...loaded,
            ...presets
              .map((preset) => preset.source)
              .filter(
                (preset) =>
                  !loaded.some(
                    (source) =>
                      source.id === preset.id ||
                      (source.type === preset.type &&
                        source.url === preset.url &&
                        source.username === preset.username),
                  ),
              ),
          ];
        }
        if (mounted) {
          setSources(loaded);
          setSourcesHydrated(true);
        }
      } catch {
        if (mounted) setSourcesHydrated(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);
  const [settings, setSettings] = useState<AppSettings>(loadSettings);
  const [favorites, setFavorites] = useState<FavoriteItem[]>(loadFavorites);
  const catalog = useCatalogWorker(settings);
  const [query, setQuery] = useState("");
  const [kinds, setKinds] = useState<string[]>(["movie", "series"]);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [onlineResults, setOnlineResults] = useState<TrendingItem[]>([]);
  const [rawMatches, setRawMatches] = useState(0);
  const [searching, setSearching] = useState(false);
  const [detail, setDetail] = useState<GroupDetail | null>(null);
  const [metadata, setMetadata] = useState<MetadataDetail | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [officialEpisodes, setOfficialEpisodes] = useState<OfficialEpisode[]>(
    [],
  );
  const [topMovies, setTopMovies] = useState<TrendingItem[]>([]);
  const [topSeries, setTopSeries] = useState<TrendingItem[]>([]);
  const [topLoading, setTopLoading] = useState(true);
  const [genreResults, setGenreResults] = useState<TrendingItem[]>([]);
  const [genreLoading, setGenreLoading] = useState(false);
  const [activeGenre, setActiveGenre] = useState("family");
  const [installPrompt, setInstallPrompt] = useState<InstallPrompt | null>(
    null,
  );
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const searchTimer = useRef<number | null>(null);
  const availabilityQueue = useRef(new Map<string, MetadataSearchItem>());
  const availabilityTimer = useRef<number | null>(null);
  const availabilityRevision = useRef(catalog.revision);
  const checkedAvailability = useRef(new Set<string>());
  if (availabilityRevision.current !== catalog.revision) {
    checkedAvailability.current.clear();
  }
  availabilityRevision.current = catalog.revision;

  const applyAvailabilityMatches = useCallback(
    (
      matches: Record<string, { servers: string[]; count: number }>,
      revision: number,
    ) => {
      const merge = (current: TrendingItem[]) => {
        let changed = false;
        const next = current.map((item) => {
          const match = matches[`${item.mediaType}-${item.id}`];
          if (!match) return item;
          const sameServers =
            (item.availableServers || []).join("\0") ===
            match.servers.join("\0");
          if (
            sameServers &&
            item.localCount === match.count &&
            item.availabilityRevision === revision
          ) {
            return item;
          }
          changed = true;
          return {
            ...item,
            availableServers: match.servers,
            localCount: match.count,
            availabilityRevision: revision,
          };
        });
        return changed ? next : current;
      };

      setTopMovies(merge);
      setTopSeries(merge);
      setGenreResults(merge);
      setOnlineResults(merge);
    },
    [],
  );

  const flushAvailabilityQueue = useCallback(async () => {
    availabilityTimer.current = null;
    const batch = Array.from(availabilityQueue.current.values());
    availabilityQueue.current.clear();
    if (!batch.length) return;

    const revision = catalog.revision;
    let matches: Record<string, { servers: string[]; count: number }> = {};
    if (!catalog.ready || !catalog.stats.total) {
      for (const item of batch) {
        availabilityQueue.current.set(`${item.mediaType}-${item.id}`, item);
      }
      return;
    }
    if (catalog.stats.total) {
      try {
        matches = await catalog.matchMetadataAvailability(
          batch.map((item) => ({
            key: `${item.mediaType}-${item.id}`,
            kind: item.mediaType === "tv" ? "series" : "movie",
            titles: [item.title, item.originalTitle].filter(
              (value, index, list): value is string =>
                Boolean(value) && list.indexOf(value) === index,
            ),
            year: item.year,
          })),
        );
      } catch (error) {
        console.warn("Falha ao verificar disponibilidade local", error);
      }
    }

    for (const item of batch) {
      const key = `${item.mediaType}-${item.id}`;
      matches[key] ||= { servers: [], count: 0 };
    }
    if (revision === availabilityRevision.current) {
      for (const item of batch) {
        checkedAvailability.current.add(`${item.mediaType}-${item.id}`);
      }
      applyAvailabilityMatches(matches, revision);
    }
  }, [
    applyAvailabilityMatches,
    catalog.matchMetadataAvailability,
    catalog.ready,
    catalog.revision,
    catalog.stats.total,
  ]);

  const queueVisibleAvailability = useCallback(
    (item: MetadataSearchItem) => {
      const current = item as TrendingItem;
      const key = `${item.mediaType}-${item.id}`;
      if (
        current.availabilityRevision === catalog.revision ||
        checkedAvailability.current.has(key)
      ) {
        return;
      }
      availabilityQueue.current.set(
        key,
        item,
      );
      if (availabilityTimer.current === null) {
        availabilityTimer.current = window.setTimeout(
          flushAvailabilityQueue,
          80,
        );
      }
    },
    [catalog.revision, flushAvailabilityQueue],
  );

  useEffect(
    () => () => {
      if (availabilityTimer.current !== null) {
        window.clearTimeout(availabilityTimer.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (sourcesHydrated) saveSources(sources);
  }, [sources, sourcesHydrated]);

  // Sincroniza navegação interna com o histórico do navegador
  useEffect(() => {
    const initialPage = pageFromLocation();
    try {
      history.replaceState({ page: initialPage }, "", `#${initialPage}`);
    } catch {
      /* ignore */
    }

    const onPop = (ev: PopStateEvent) => {
      const statePage = ev.state?.page;
      const nextPage = PAGES.includes(statePage) ? statePage : pageFromLocation();
      setPage(nextPage);
      setDetail(null);
      setMetadata(null);
      setOfficialEpisodes([]);
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Ao mudar de página dentro do app, cria uma entrada no history
  useEffect(() => {
    try {
      const st = history.state as { page?: Page; view?: string } | null;
      if (!st || st.page !== page || st.view === "detail") {
        history.pushState({ page }, "", `#${page}`);
      }
    } catch {
      /* ignore */
    }
  }, [page]);
  useEffect(() => {
    saveSettings(settings);
    const root = document.documentElement;
    const theme =
      settings.theme === "system"
        ? matchMedia("(prefers-color-scheme: light)").matches
          ? "light"
          : "dark"
        : settings.theme;
    root.dataset.theme = theme;
  }, [settings]);
  useEffect(() => {
    saveFavorites(favorites);
  }, [favorites]);
  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPrompt);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  useEffect(() => {
    const query = matchMedia("(display-mode: standalone)");
    const update = () =>
      setIsStandalone(
        query.matches ||
          Boolean(
            (navigator as Navigator & { standalone?: boolean }).standalone,
          ),
      );
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    Promise.allSettled([getTop("movie"), getTop("tv")]).then(
      ([movies, series]) => {
        setTopMovies(
          movies.status === "fulfilled" && Array.isArray(movies.value)
            ? movies.value
            : [],
        );
        setTopSeries(
          series.status === "fulfilled" && Array.isArray(series.value)
            ? series.value
            : [],
        );
        setTopLoading(false);
      },
    );
  }, []);
  useEffect(() => {
    loadGenreRecommendations("family");
  }, []);

  async function runSearch(nextQuery = query, overrideKinds = kinds) {
    const value = nextQuery.trim();
    if (!value) {
      setResults([]);
      setOnlineResults([]);
      setRawMatches(0);
      return;
    }
    setSearching(true);
    try {
      const wantsMovies = overrideKinds.includes("movie");
      const wantsSeries = overrideKinds.includes("series");
      const onlineTasks: Promise<MetadataSearchItem[]>[] = [];
      if (wantsMovies) onlineTasks.push(searchMetadata(value, "movie"));
      if (wantsSeries) onlineTasks.push(searchMetadata(value, "tv"));
      const [local, online] = await Promise.allSettled([
        catalog.search(value, overrideKinds),
        Promise.allSettled(onlineTasks),
      ]);
      if (local.status === "fulfilled") {
        setResults(local.value.results);
        setRawMatches(local.value.rawMatches);
      } else {
        setResults([]);
        setRawMatches(0);
      }
      const onlineItems =
        online.status === "fulfilled"
          ? online.value.flatMap((result) =>
              result.status === "fulfilled" ? result.value : [],
            )
          : [];
      setOnlineResults(dedupeMetadata(onlineItems));
      setPage("search");
    } finally {
      setSearching(false);
    }
  }

  async function loadGenreRecommendations(genre: string) {
    setActiveGenre(genre);
    setGenreLoading(true);
    try {
      const [movies, series] = await Promise.allSettled([
        discoverMetadata("movie", genre),
        discoverMetadata("tv", genre),
      ]);
      const items = [
        ...(movies.status === "fulfilled" ? movies.value : []),
        ...(series.status === "fulfilled" ? series.value : []),
      ];
      setGenreResults(dedupeMetadata(items).slice(0, 12));
    } finally {
      setGenreLoading(false);
    }
  }

  function scheduleSearch(value: string) {
    setQuery(value);
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => runSearch(value), 260);
  }

  async function refreshSeasonAvailability(local: GroupDetail, season: number) {
    if (local.kind !== "series") return local;
    const credentials = sources
      .filter((source) => source.type === "xtream")
      .map((source) => ({ source, password: loadXtreamSession(source.id) }))
      .filter((entry) => entry.password);
    if (!credentials.length) return local;
    try {
      const remoteEpisodes = await catalog.getSeasonAvailability(
        local.groupKey,
        season,
        credentials,
      );
      if (!remoteEpisodes.length) return local;
      const updated = mergeSeasonEpisodes(local, remoteEpisodes, season);
      setDetail((current) =>
        current?.groupKey === local.groupKey ? updated : current,
      );
      return updated;
    } catch (error) {
      console.warn(error);
      return local;
    }
  }

  async function openDetail(result: SearchResult) {
    setMetadata(null);
    setOfficialEpisodes([]);
    setMetadataLoading(true);
    const initialDetail: GroupDetail = {
      groupKey: result.groupKey,
      kind: result.kind,
      title: result.title,
      year: result.year,
      logo: result.logo,
      servers: result.servers,
      categories: result.categories,
      episodes: [],
      originalNames: result.originalNames,
    };
    setDetail(initialDetail);
    try {
      history.pushState(
        { page, view: "detail" },
        "",
        `#${page}/detail`,
      );
    } catch {}
    let local = initialDetail;
    try {
      local = await catalog.getGroupDetail(result.groupKey);
      setDetail(local);
    } catch (error) {
      console.warn(error);
    }
    try {
      const type = local.kind === "series" ? "tv" : "movie";
      const candidates = await searchMetadata(local.title, type, local.year);
      if (candidates[0]) {
        const data = await getMetadataDetails(type, candidates[0].id);
        setMetadata(data);
        if (type === "tv") {
          const firstLocalSeason =
            local.episodes[0]?.season ||
            data.seasons?.find((s) => s.seasonNumber > 0)?.seasonNumber ||
            1;
          setSelectedSeason(firstLocalSeason);
          await Promise.all([
            refreshSeasonAvailability(local, firstLocalSeason),
            getSeasonEpisodes(data.id, firstLocalSeason).then(setOfficialEpisodes),
          ]);
        }
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setMetadataLoading(false);
    }
  }

  async function openMetadataDetail(item: TrendingItem) {
    setMetadata(null);
    setOfficialEpisodes([]);
    setMetadataLoading(true);
    const kind = item.mediaType === "tv" ? "series" : "movie";
    const initialDetail: GroupDetail = {
      groupKey: `metadata|${item.mediaType}|${item.id}`,
      kind,
      title: item.title,
      year: item.year,
      logo: item.posterUrl,
      servers: item.availableServers || [],
      categories: ["Pesquisa online"],
      episodes: [],
    };
    setDetail(initialDetail);
    try {
      history.pushState(
        { page, view: "detail" },
        "",
        `#${page}/detail`,
      );
    } catch {}
    const names = [item.title, item.originalTitle].filter(
      (value, index, list): value is string =>
        Boolean(value) && list.indexOf(value) === index,
    );
    let localDetail: GroupDetail | null = null;
    try {
      const responses = await Promise.all(
        names.map((name) =>
          catalog
            .search(name, [kind])
            .catch(() => ({ results: [], rawMatches: 0 })),
        ),
      );
      const match = responses
        .flatMap((response) => response.results)
        .find((result) => result.kind === kind);
      if (match) localDetail = await catalog.getGroupDetail(match.groupKey);
    } catch {
      localDetail = null;
    }
    if (localDetail) {
      applyAvailabilityMatches(
        {
          [`${item.mediaType}-${item.id}`]: {
            servers: localDetail.servers,
            count: Math.max(localDetail.episodes.length, 1),
          },
        },
        catalog.revision,
      );
      setDetail({
        ...localDetail,
        logo: localDetail.logo || item.posterUrl,
      });
    }
    try {
      const data = await getMetadataDetails(item.mediaType, item.id);
      setMetadata(data);
      if (item.mediaType === "tv") {
        const firstSeason =
          data.seasons?.find((season) => season.seasonNumber > 0)
            ?.seasonNumber || 1;
        setSelectedSeason(firstSeason);
        await Promise.all([
          localDetail
            ? refreshSeasonAvailability(localDetail, firstSeason)
            : Promise.resolve(),
          getSeasonEpisodes(data.id, firstSeason).then(setOfficialEpisodes),
        ]);
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setMetadataLoading(false);
    }
  }

  async function changeSeason(season: number) {
    setSelectedSeason(season);
    setOfficialEpisodes([]);
    const currentDetail = detail;
    if (currentDetail?.kind === "series") {
      refreshSeasonAvailability(currentDetail, season);
    }
    if (metadata?.mediaType === "tv") {
      try {
        setOfficialEpisodes(await getSeasonEpisodes(metadata.id, season));
      } catch {
        setOfficialEpisodes([]);
      }
    }
  }

  function addSource(source: SourceConfig) {
    setSources((current) => [
      ...current.filter((s) => s.id !== source.id),
      source,
    ]);
  }
  function updateSourceAfterSync(id: string, itemCount?: number) {
    setSources((current) =>
      current.map((source) =>
        source.id === id
          ? { ...source, lastSyncAt: new Date().toISOString(), itemCount }
          : source,
      ),
    );
  }
  useEffect(() => {
    const progress = catalog.progress;
    if (progress?.done && progress.sourceId && !progress.error)
      updateSourceAfterSync(progress.sourceId, progress.current);
  }, [
    catalog.progress?.done,
    catalog.progress?.sourceId,
    catalog.progress?.current,
    catalog.progress?.error,
  ]);
  async function requestInstall() {
    if (!installPrompt) {
      setInstallHelpOpen(true);
      return;
    }
    await installPrompt.prompt();
    setInstallPrompt(null);
  }
  async function removeSource(source: SourceConfig) {
    if (!confirm(`Remover ${source.name} e seus itens locais?`)) return;
    catalog.removeSource(source.id);
    setSources((current) => current.filter((s) => s.id !== source.id));
  }

  const favoriteKeys = useMemo(
    () => new Set(favorites.map((item) => item.favoriteKey)),
    [favorites],
  );

  const toggleFavorite = useCallback((item: FavoriteItem) => {
    setFavorites((current) => {
      if (current.some((favorite) => favorite.favoriteKey === item.favoriteKey)) {
        return current.filter((favorite) => favorite.favoriteKey !== item.favoriteKey);
      }
      return [{ ...item, addedAt: new Date().toISOString() }, ...current];
    });
  }, []);

  const removeFavorite = useCallback((favoriteKey: string) => {
    setFavorites((current) =>
      current.filter((favorite) => favorite.favoriteKey !== favoriteKey),
    );
  }, []);

  async function openFavorite(item: FavoriteItem) {
    if (item.source === "metadata" && item.metadataId && item.mediaType) {
      await openMetadataDetail({
        id: item.metadataId,
        mediaType: item.mediaType,
        title: item.title,
        originalTitle: item.originalTitle,
        year: item.year,
        overview: item.overview,
        posterUrl: item.logo,
        availableServers: item.servers,
      });
      return;
    }
    await openDetail({
      groupKey: item.groupKey,
      kind: item.kind,
      title: item.title,
      year: item.year,
      logo: item.logo,
      score: 1,
      count: Math.max(1, item.servers.length),
      servers: item.servers,
      categories: item.categories,
    });
  }

  const pageTitle =
    NAV.find((item) => item.id === page)?.label || "StreamRadar";
  const completeness = useMemo(
    () =>
      detail?.kind === "series"
        ? buildEpisodeRows(detail, officialEpisodes, selectedSeason)
        : [],
    [detail, officialEpisodes, selectedSeason],
  );

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <button
          className="brand"
          onClick={() => setPage("home")}
          aria-label="StreamRadar - início"
        >
          <span className="brand-mark">
            <Radar size={28} />
          </span>
          <span>
            <b>StreamRadar</b>
            <small>Catalog intelligence</small>
          </span>
        </button>
        <nav className="side-nav" aria-label="Navegação principal">
          {NAV.map((item) => (
            <button
              key={item.id}
              className={page === item.id ? "active" : ""}
              onClick={() => setPage(item.id)}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-card">
          <ShieldCheck size={22} />
          <div>
            <b>Somente catálogo</b>
            <span>Não reproduz nem armazena links de stream.</span>
          </div>
        </div>
        <div className="sidebar-footer">
          <span className={navigator.onLine ? "dot online" : "dot"} />
          {navigator.onLine ? "Online" : "Offline"}
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div>
            <span className="eyebrow">STREAMRADAR</span>
            <h1>{pageTitle}</h1>
          </div>
          <div className="top-actions">
            {!isStandalone && (
              <button className="button soft" onClick={requestInstall}>
                <Download size={18} />
                Instalar
              </button>
            )}
            <button
              className="icon-button"
              onClick={() =>
                setSettings((s) => ({
                  ...s,
                  theme: s.theme === "dark" ? "light" : "dark",
                }))
              }
              aria-label="Alternar tema"
            >
              {settings.theme === "light" ? <Moon /> : <Sun />}
            </button>
          </div>
        </header>

        {catalog.progress && (
          <div
            className={`sync-banner ${catalog.progress.error ? "error" : ""}`}
          >
            <RefreshCw
              className={
                !catalog.progress.done && !catalog.progress.error ? "spin" : ""
              }
              size={18}
            />
            <span>{catalog.progress.message}</span>
          </div>
        )}

        {page === "home" && (
          <HomePage
            stats={catalog.stats}
            sources={sources}
            topMovies={topMovies || []}
            topSeries={topSeries || []}
            topLoading={topLoading}
            genreResults={genreResults}
            genreLoading={genreLoading}
            activeGenre={activeGenre}
            onGenre={loadGenreRecommendations}
            onSearch={(value: string, only: string[]) => {
              setKinds(only);
              setQuery(value);
              runSearch(value, only);
            }}
            onOpenMetadata={openMetadataDetail}
            onVisibleMetadata={queueVisibleAvailability}
            availabilityRevision={catalog.revision}
            onNavigate={setPage}
            favoriteKeys={favoriteKeys}
            onToggleFavorite={toggleFavorite}
          />
        )}
        {page === "search" && (
          <SearchPage
            query={query}
            setQuery={scheduleSearch}
            kinds={kinds}
            setKinds={setKinds}
            results={results}
            onlineResults={onlineResults}
            rawMatches={rawMatches}
            searching={searching}
            onSearch={(nextKinds?: string[]) =>
              runSearch(query, nextKinds || kinds)
            }
            onOpen={openDetail}
            onOpenMetadata={openMetadataDetail}
            onVisibleMetadata={queueVisibleAvailability}
            availabilityRevision={catalog.revision}
            favoriteKeys={favoriteKeys}
            onToggleFavorite={toggleFavorite}
          />
        )}
        {page === "series" && (
          <SeriesPage
            onSearch={(value) => {
              setKinds(["series"]);
              setQuery(value);
              runSearch(value, ["series"]);
            }}
          />
        )}
        {page === "favorites" && (
          <FavoritesPage
            favorites={favorites}
            onOpen={openFavorite}
            onRemove={removeFavorite}
            onToggleFavorite={toggleFavorite}
          />
        )}
        {page === "sources" && (
          <SourcesPage
            sources={sources}
            addSource={addSource}
            removeSource={removeSource}
            catalog={catalog}
            updateSourceAfterSync={updateSourceAfterSync}
          />
        )}
        {page === "settings" && (
          <SettingsPage
            settings={settings}
            setSettings={setSettings}
            sources={sources}
            clearCatalog={catalog.clearCatalog}
          />
        )}
      </main>

      <nav className="bottom-nav" aria-label="Navegação móvel">
        {NAV.map((item) => (
          <button
            key={item.id}
            className={page === item.id ? "active" : ""}
            onClick={() => setPage(item.id)}
          >
            <item.icon />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {installHelpOpen && (
        <InstallHelpModal onClose={() => setInstallHelpOpen(false)} />
      )}
      {detail && (
        <DetailDrawer
          detail={detail}
          metadata={metadata}
          loading={metadataLoading}
          selectedSeason={selectedSeason}
          changeSeason={changeSeason}
          rows={completeness}
          isFavorite={favoriteKeys.has(favoriteKeyFromDetail(detail))}
          onToggleFavorite={() => toggleFavorite(favoriteFromDetail(detail, metadata))}
          onClose={() => {
            setDetail(null);
            setMetadata(null);
            setOfficialEpisodes([]);
            try {
              if (history.state?.view === "detail") history.back();
              else history.replaceState({ page }, "", `#${page}`);
            } catch {}
          }}
        />
      )}
    </div>
  );
}

function InstallHelpModal({ onClose }: { onClose: () => void }) {
  const isApple = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  return (
    <div
      className="install-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-title"
    >
      <div className="install-modal">
        <button
          className="icon-button install-modal-close"
          onClick={onClose}
          aria-label="Fechar"
        >
          <X size={18} />
        </button>
        <span className="brand-mark">
          <Radar size={28} />
        </span>
        <div>
          <span className="eyebrow">INSTALAR APP</span>
          <h2 id="install-title">Adicionar StreamRadar à tela inicial</h2>
          <p>
            Este navegador não liberou o instalador automático agora. Ainda dá
            para instalar manualmente pelo menu do navegador.
          </p>
        </div>
        <ol>
          {isApple && (
            <>
              <li>Abra o site no Safari.</li>
              <li>Toque em Compartilhar.</li>
              <li>Escolha “Adicionar à Tela de Início”.</li>
            </>
          )}
          {isAndroid && (
            <>
              <li>Abra o site no Chrome.</li>
              <li>Toque no menu de três pontos.</li>
              <li>Escolha “Instalar app” ou “Adicionar à tela inicial”.</li>
            </>
          )}
          {!isApple && !isAndroid && (
            <>
              <li>Abra o menu do navegador.</li>
              <li>Procure por “Instalar app”.</li>
              <li>Confirme para abrir em modo aplicativo.</li>
            </>
          )}
        </ol>
        <button className="button primary" onClick={onClose}>
          Entendi
        </button>
      </div>
    </div>
  );
}

function HomePage({
  stats,
  sources,
  topMovies,
  topSeries,
  topLoading,
  genreResults,
  genreLoading,
  activeGenre,
  onGenre,
  onSearch,
  onOpenMetadata,
  onVisibleMetadata,
  availabilityRevision,
  onNavigate,
  favoriteKeys,
  onToggleFavorite,
}: any) {
  return (
    <div className="page-stack">
      <section className="hero-panel">
        <div className="hero-copy">
          <span className="pill">
            <Sparkles size={15} />
            Busca unificada
          </span>
          <h2>Busque nos servidores e no catálogo online.</h2>
          <div className="hero-actions">
            <button
              className="button primary"
              onClick={() => onNavigate("search")}
            >
              <Search size={19} />
              Começar pesquisa
            </button>
            <span className="hero-stats" aria-label="Resumo do catálogo">
              <span className="hero-stat">
                <Database size={16} />
                <span>{formatNumber(stats.total)} indexados</span>
              </span>
              <span className="hero-stat">
                <Film size={16} />
                <span>{formatNumber(stats.movie)} filmes</span>
              </span>
              <span className="hero-stat">
                <ListVideo size={16} />
                <span>{formatNumber(stats.series)} séries</span>
              </span>
            </span>
          </div>
        </div>
        <div className="radar-visual">
          <span className="sweep" />
          <span className="blip b1" />
          <span className="blip b2" />
          <span className="blip b3" />
          <Radar size={92} />
        </div>
      </section>
      <section className="section-block">
        <div className="section-heading">
          <div>
            <span className="eyebrow">RECOMENDAÇÕES</span>
            <h2>Escolha por gênero</h2>
          </div>
        </div>
        <div className="genre-row">
          {GENRE_PRESETS.map((genre) => (
            <button
              key={genre.id}
              className={
                activeGenre === genre.id ? "genre-chip active" : "genre-chip"
              }
              onClick={() => onGenre(genre.id)}
            >
              {genre.label}
            </button>
          ))}
        </div>
        <MediaRail
          items={genreResults}
          label={
            GENRE_PRESETS.find((genre) => genre.id === activeGenre)?.query ||
            "recomendados"
          }
          loading={genreLoading}
          onSelect={onOpenMetadata}
          onVisible={onVisibleMetadata}
          availabilityRevision={availabilityRevision}
          favoriteKeys={favoriteKeys}
          onToggleFavorite={onToggleFavorite}
          onSearch={(item: MetadataSearchItem) =>
            onSearch(item.title, [item.mediaType === "tv" ? "series" : "movie"])
          }
        />
      </section>
      <section className="section-block">
        <div className="section-heading">
          <div>
            <span className="eyebrow">DESCOBRIR</span>
            <h2>Em alta e disponível para pesquisar</h2>
          </div>
        </div>
        <MediaRail
          items={topMovies}
          label="Filmes"
          loading={topLoading}
          onSelect={onOpenMetadata}
          onVisible={onVisibleMetadata}
          availabilityRevision={availabilityRevision}
          favoriteKeys={favoriteKeys}
          onToggleFavorite={onToggleFavorite}
          onSearch={(item: MetadataSearchItem) =>
            onSearch(item.title, ["movie"])
          }
        />
        <MediaRail
          items={topSeries}
          label="Séries"
          loading={topLoading}
          onSelect={onOpenMetadata}
          onVisible={onVisibleMetadata}
          availabilityRevision={availabilityRevision}
          favoriteKeys={favoriteKeys}
          onToggleFavorite={onToggleFavorite}
          onSearch={(item: MetadataSearchItem) =>
            onSearch(item.title, ["series"])
          }
        />
      </section>
      <section className="two-col">
        <div className="panel">
          <div className="panel-title">
            <Server />
            <div>
              <h3>Fontes locais</h3>
              <p>{sources.length} cadastrada(s)</p>
            </div>
            <button
              className="icon-button"
              onClick={() => onNavigate("sources")}
            >
              <ChevronRight />
            </button>
          </div>
          <div className="source-mini-list">
            {sources.slice(0, 4).map((s: SourceConfig) => (
              <div key={s.id}>
                <span className="source-avatar">
                  {s.name.slice(0, 2).toUpperCase()}
                </span>
                <div>
                  <b>{s.name}</b>
                  <small>
                    {s.type} •{" "}
                    {s.itemCount
                      ? `${formatNumber(s.itemCount)} itens`
                      : "aguardando sincronização"}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="panel accent-panel">
          <Activity />
          <h3>Catálogo local-first</h3>
          <p>
            Os itens ficam no IndexedDB para continuar pesquisando mesmo quando
            a internet cair. Apenas os links de fontes ficam no localStorage.
          </p>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, value, label }: any) {
  return (
    <div className="stat-card">
      <span className="stat-icon">
        <Icon />
      </span>
      <strong>{formatNumber(value)}</strong>
      <span>{label}</span>
    </div>
  );
}

function scrollRail(event: MouseEvent<HTMLButtonElement>, direction: -1 | 1) {
  const rail = event.currentTarget
    .closest(".rail-wrap")
    ?.querySelector(".media-rail") as HTMLElement | null;
  rail?.scrollBy({
    left: direction * Math.max(rail.clientWidth * 0.85, 360),
    behavior: "smooth",
  });
}

function useAvailabilityProbe<T extends HTMLElement>(
  item: MetadataSearchItem,
  onVisible: (item: MetadataSearchItem) => void,
  revision: number,
) {
  const ref = useRef<T | null>(null);
  const identity = [
    item.mediaType,
    item.id,
    item.title,
    item.originalTitle || "",
    item.year || "",
  ].join("|");

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      onVisible(item);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisible(item);
          observer.disconnect();
        }
      },
      { rootMargin: "180px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [identity, onVisible, revision]);

  return ref;
}

function MediaRailItem({
  item,
  index,
  label,
  onSelect,
  onSearch,
  onVisible,
  availabilityRevision,
  favoriteKeys,
  onToggleFavorite,
}: {
  item: TrendingItem;
  index: number;
  label: string;
  onSelect: (item: MetadataSearchItem) => void;
  onSearch: (item: MetadataSearchItem) => void;
  onVisible: (item: MetadataSearchItem) => void;
  availabilityRevision: number;
  favoriteKeys: Set<string>;
  onToggleFavorite: (item: FavoriteItem) => void;
}) {
  const probeRef = useAvailabilityProbe<HTMLButtonElement>(
    item,
    onVisible,
    availabilityRevision,
  );
  const checked = item.availabilityRevision === availabilityRevision;
  const isFavorite = favoriteKeys.has(favoriteKeyFromMetadata(item));
  const { longPressHandlers, consumeLongPressClick } = useLongPressFavorite(() =>
    onToggleFavorite(favoriteFromMetadata(item)),
  );

  return (
    <button
      ref={probeRef}
      className="mini-poster"
      onClick={() => {
        if (consumeLongPressClick()) return;
        onSelect(item);
      }}
      onDoubleClick={() => onSearch(item)}
      title={
        isFavorite
          ? "Segure para remover dos favoritos"
          : "Segure para adicionar aos favoritos"
      }
      {...longPressHandlers}
    >
      <PosterImage
        src={item.posterUrl}
        alt=""
        fallback={<span className="mini-poster-fallback">{label}</span>}
      />
      <span className="rank">{index + 1}</span>
      <FavoriteMark active={isFavorite} />
      <div>
        <b>{item.title}</b>
        <small>{item.year || "—"}</small>
        {item.availableServers?.length ? (
          <span className="availability-mini">
            {item.availableServers.slice(0, 2).join(", ")}
            {item.availableServers.length > 2
              ? ` +${item.availableServers.length - 2}`
              : ""}
          </span>
        ) : (
          <span className="availability-mini muted">
            {checked
              ? "Não encontrado nos servidores"
              : "Verificando nos servidores..."}
          </span>
        )}
      </div>
    </button>
  );
}

function MediaRail({
  items,
  label,
  loading,
  onSelect,
  onSearch,
  onVisible,
  availabilityRevision,
  favoriteKeys,
  onToggleFavorite,
}: {
  items: TrendingItem[];
  label: string;
  loading?: boolean;
  onSelect: (item: MetadataSearchItem) => void;
  onSearch: (item: MetadataSearchItem) => void;
  onVisible: (item: MetadataSearchItem) => void;
  availabilityRevision: number;
  favoriteKeys: Set<string>;
  onToggleFavorite: (item: FavoriteItem) => void;
}) {
  if (!items.length)
    return (
      <div className="rail-wrap">
        <h3>{label}</h3>
        {loading ? (
          <div className="rail-skeleton" />
        ) : (
          <div className="rail-empty">
            Não foi possível carregar os destaques agora.
          </div>
        )}
      </div>
    );
  return (
    <div className="rail-wrap">
      <div className="rail-head">
        <h3>{label}</h3>
        <div className="rail-actions">
          <button
            className="icon-button"
            onClick={(event) => scrollRail(event, -1)}
            aria-label={`Voltar ${label}`}
          >
            <ArrowLeft size={17} />
          </button>
          <button
            className="icon-button"
            onClick={(event) => scrollRail(event, 1)}
            aria-label={`Avançar ${label}`}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div className="media-rail">
        {items.slice(0, 10).map((item, index) => (
          <MediaRailItem
            key={`${label}-${item.id}`}
            item={item}
            index={index}
            label={label}
            onSelect={onSelect}
            onSearch={onSearch}
            onVisible={onVisible}
            availabilityRevision={availabilityRevision}
            favoriteKeys={favoriteKeys}
            onToggleFavorite={onToggleFavorite}
          />
        ))}
      </div>
    </div>
  );
}

function SearchPage({
  query,
  setQuery,
  kinds,
  setKinds,
  results,
  onlineResults,
  rawMatches,
  searching,
  onSearch,
  onOpen,
  onOpenMetadata,
  onVisibleMetadata,
  availabilityRevision,
  favoriteKeys,
  onToggleFavorite,
}: any) {
  const toggles = [
    { id: "movie", label: "Filmes", icon: Film },
    { id: "series", label: "Séries", icon: ListVideo },
  ];
  const [sort, setSort] = useState<"relevance" | "newest" | "oldest" | "title">(
    "relevance",
  );
  const sortedResults = useMemo(
    () => sortLocalResults(results, sort),
    [results, sort],
  );
  const sortedOnlineResults = useMemo(
    () => sortMetadataResults(onlineResults, sort),
    [onlineResults, sort],
  );
  const hasAnyResults = sortedResults.length || sortedOnlineResults.length;
  function toggleKind(id: string) {
    const next = kinds.includes(id)
      ? kinds.length > 1
        ? kinds.filter((kind: string) => kind !== id)
        : kinds
      : [...kinds, id];
    setKinds(next);
    if (query.trim()) onSearch(next);
  }
  return (
    <div className="page-stack">
      <section className="search-hero">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
          className="search-box"
        >
          <Search />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Busque no IPTV e no TMDb por título, ano, ator ou episódio..."
          />
          <button className="button primary">Buscar</button>
        </form>
        <div className="filter-row">
          {toggles.map((t) => (
            <button
              key={t.id}
              className={kinds.includes(t.id) ? "filter active" : "filter"}
              onClick={() => toggleKind(t.id)}
            >
              <t.icon size={17} />
              {t.label}
            </button>
          ))}
        </div>
        <div className="filter-row sort-row">
          <span>Ordenar</span>
          <button
            className={sort === "relevance" ? "filter active" : "filter"}
            onClick={() => setSort("relevance")}
          >
            Relevância
          </button>
          <button
            className={sort === "newest" ? "filter active" : "filter"}
            onClick={() => setSort("newest")}
          >
            Mais recentes
          </button>
          <button
            className={sort === "oldest" ? "filter active" : "filter"}
            onClick={() => setSort("oldest")}
          >
            Mais antigos
          </button>
          <button
            className={sort === "title" ? "filter active" : "filter"}
            onClick={() => setSort("title")}
          >
            A-Z
          </button>
        </div>
      </section>
      <div className="results-head">
        <div>
          <h2>
            {query ? `Resultados para “${query}”` : "Pesquise no catálogo"}
          </h2>
          <p>
            {rawMatches
              ? `${formatNumber(rawMatches)} ocorrências locais em ${formatNumber(results.length)} cards`
              : "A busca também consulta filmes e séries online."}
          </p>
        </div>
        {searching && <RefreshCw className="spin" />}
      </div>
      {!!sortedResults.length && (
        <section className="section-block">
          <h3 className="compact-title">Nos seus servidores</h3>
          <div className="poster-grid">
            {sortedResults.map((result: SearchResult) => (
              <ResultCard
                key={result.groupKey}
                result={result}
                onOpen={() => onOpen(result)}
                isFavorite={favoriteKeys.has(result.groupKey)}
                onToggleFavorite={() =>
                  onToggleFavorite(favoriteFromSearchResult(result))
                }
              />
            ))}
          </div>
        </section>
      )}
      {!!sortedOnlineResults.length && (
        <section className="section-block">
          <h3 className="compact-title">Catálogo online</h3>
          <div className="online-grid">
            {sortedOnlineResults.map((item: TrendingItem) => (
              <OnlineMetadataCard
                key={`${item.mediaType}-${item.id}`}
                item={item}
                onOpen={() => onOpenMetadata(item)}
                onVisible={onVisibleMetadata}
                availabilityRevision={availabilityRevision}
                isFavorite={favoriteKeys.has(favoriteKeyFromMetadata(item))}
                onToggleFavorite={() => onToggleFavorite(favoriteFromMetadata(item))}
              />
            ))}
          </div>
        </section>
      )}
      {query && !searching && !hasAnyResults && (
        <EmptyState
          title="Nada encontrado"
          text="Tente o título original, ano ou ajuste os filtros."
        />
      )}
    </div>
  );
}

function sortLocalResults(
  results: SearchResult[],
  sort: "relevance" | "newest" | "oldest" | "title",
) {
  const list = [...results];
  if (sort === "newest")
    return list.sort(
      (a, b) => (b.year || 0) - (a.year || 0) || b.score - a.score,
    );
  if (sort === "oldest")
    return list.sort(
      (a, b) => (a.year || 9999) - (b.year || 9999) || b.score - a.score,
    );
  if (sort === "title")
    return list.sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));
  return list.sort((a, b) => b.score - a.score || b.count - a.count);
}

function sortMetadataResults(
  results: TrendingItem[],
  sort: "relevance" | "newest" | "oldest" | "title",
) {
  const list = [...results];
  if (sort === "newest")
    return list.sort(
      (a, b) =>
        (b.year || 0) - (a.year || 0) ||
        (b.popularity || 0) - (a.popularity || 0),
    );
  if (sort === "oldest")
    return list.sort(
      (a, b) =>
        (a.year || 9999) - (b.year || 9999) ||
        (b.popularity || 0) - (a.popularity || 0),
    );
  if (sort === "title")
    return list.sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));
  return list.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
}

function OnlineMetadataCard({
  item,
  onOpen,
  onVisible,
  availabilityRevision,
  isFavorite,
  onToggleFavorite,
}: {
  item: TrendingItem;
  onOpen: () => void;
  onVisible: (item: MetadataSearchItem) => void;
  availabilityRevision: number;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}) {
  const probeRef = useAvailabilityProbe<HTMLElement>(
    item,
    onVisible,
    availabilityRevision,
  );
  const checked = item.availabilityRevision === availabilityRevision;
  const { longPressHandlers, consumeLongPressClick } =
    useLongPressFavorite(onToggleFavorite);

  return (
    <article
      ref={probeRef}
      className="online-card"
      role="button"
      tabIndex={0}
      onClick={() => {
        if (consumeLongPressClick()) return;
        onOpen();
      }}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      title={
        isFavorite
          ? "Segure para remover dos favoritos"
          : "Segure para adicionar aos favoritos"
      }
      {...longPressHandlers}
    >
      <span className="online-poster-wrap">
        <PosterImage
          src={item.posterUrl}
          alt={`Capa de ${item.title}`}
          fallback={
            <span className="online-poster-fallback">
              <Radar />
            </span>
          }
        />
        <FavoriteMark active={isFavorite} />
      </span>
      <div className="online-card-content">
        <div className="source-chips meta-chips">
          <span
            className={
              item.mediaType === "tv" ? "chip-type series" : "chip-type movie"
            }
          >
            {item.mediaType === "tv" ? "SÉRIE TMDb" : "FILME TMDb"}
          </span>
          {item.year && <span className="chip-year">{item.year}</span>}
          <span className="muted-chip">Detalhes online</span>
        </div>
        <h3>{item.title}</h3>
        {item.originalTitle && item.originalTitle !== item.title && (
          <small>{item.originalTitle}</small>
        )}
        <p>{item.overview || "Sinopse ainda não disponível."}</p>
        {item.availableServers?.length ? (
          <span className="availability-mini">
            Disponível em {item.availableServers.slice(0, 3).join(", ")}
            {item.availableServers.length > 3
              ? ` +${item.availableServers.length - 3}`
              : ""}
          </span>
        ) : (
          <span className="availability-mini muted">
            {checked
              ? "Não encontrado nos servidores"
              : "Verificando nos servidores..."}
          </span>
        )}
      </div>
    </article>
  );
}

function ResultCard({
  result,
  onOpen,
  isFavorite,
  onToggleFavorite,
}: {
  result: SearchResult;
  onOpen: () => void;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}) {
  const { longPressHandlers, consumeLongPressClick } =
    useLongPressFavorite(onToggleFavorite);

  return (
    <article
      className="media-card"
      role="button"
      onClick={() => {
        if (consumeLongPressClick()) return;
        onOpen();
      }}
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      title={
        isFavorite
          ? "Segure para remover dos favoritos"
          : "Segure para adicionar aos favoritos"
      }
      {...longPressHandlers}
    >
      <div className="poster-area">
        <PosterImage
          src={result.logo}
          alt={`Capa de ${result.title}`}
          fallback={
            <div className="poster-placeholder">
              <Radar />
              <span>{posterFallback(result.kind)}</span>
            </div>
          }
        />
        <div className="poster-overlay" />
        <FavoriteMark active={isFavorite} />
        <span className={`type-badge ${result.kind}`}>
          {result.kind === "series"
            ? "SÉRIE"
            : result.kind === "movie"
              ? "FILME"
              : "CANAL"}
        </span>
        {result.year && <span className="year-badge">{result.year}</span>}
      </div>
      <div className="media-card-body">
        <h3>{result.title}</h3>
        <p>
          {result.kind === "series"
            ? `${result.episodeCount || result.count} episódios • ${result.seasonCount || 0} temporadas`
            : `${result.count} ocorrência(s)`}
        </p>
        <div className="source-chips">
          {result.servers.slice(0, 3).map((server) => (
            <span key={server}>{server}</span>
          ))}
          {result.servers.length > 3 && (
            <span>+{result.servers.length - 3}</span>
          )}
        </div>
      </div>
    </article>
  );
}

function FavoritesPage({
  favorites,
  onOpen,
  onRemove,
  onToggleFavorite,
}: {
  favorites: FavoriteItem[];
  onOpen: (item: FavoriteItem) => void;
  onRemove: (favoriteKey: string) => void;
  onToggleFavorite: (item: FavoriteItem) => void;
}) {
  const sortedFavorites = useMemo(
    () =>
      [...favorites].sort(
        (a, b) =>
          new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(),
      ),
    [favorites],
  );

  return (
    <div className="page-stack">
      <section className="section-block">
        <div className="section-heading">
          <div>
            <span className="eyebrow">MINHA LISTA</span>
            <h2>Favoritos para assistir</h2>
          </div>
          <span className="favorites-count">
            {formatNumber(favorites.length)} salvo(s)
          </span>
        </div>
        {sortedFavorites.length ? (
          <div className="poster-grid">
            {sortedFavorites.map((favorite) => (
              <div className="favorite-card-wrap" key={favorite.favoriteKey}>
                <ResultCard
                  result={{
                    groupKey: favorite.groupKey,
                    kind: favorite.kind,
                    title: favorite.title,
                    year: favorite.year,
                    logo: favorite.logo,
                    score: 1,
                    count: Math.max(1, favorite.servers.length),
                    servers: favorite.servers,
                    categories: favorite.categories,
                  }}
                  onOpen={() => onOpen(favorite)}
                  isFavorite
                  onToggleFavorite={() => onToggleFavorite(favorite)}
                />
                <button
                  className="icon-button favorite-remove"
                  onClick={() => onRemove(favorite.favoriteKey)}
                  aria-label={`Remover ${favorite.title} dos favoritos`}
                  title="Remover dos favoritos"
                >
                  <X size={17} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Nenhum favorito ainda"
            text="Segure a capa de um filme ou série para salvar aqui."
          />
        )}
      </section>
    </div>
  );
}

function SeriesPage({ onSearch }: { onSearch: (value: string) => void }) {
  const [value, setValue] = useState("");
  return (
    <div className="page-stack">
      <section className="feature-banner">
        <div>
          <span className="pill">
            <ListVideo size={15} />
            CONTROLE DE EPISÓDIOS
          </span>
          <h2>Uma série, um card, todos os servidores.</h2>
          <p>
            Abra a série para comparar os episódios locais com a lista oficial e
            identificar o que está presente, faltando ou ainda não foi lançado.
          </p>
        </div>
        <Layers3 size={88} />
      </section>
      <form
        className="search-box panel"
        onSubmit={(e) => {
          e.preventDefault();
          if (value) onSearch(value);
        }}
      >
        <Search />
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Ex.: Os 100, The Last of Us, Reacher..."
        />
        <button className="button primary">Localizar série</button>
      </form>
      <div className="three-col">
        <Feature
          icon={Check}
          title="Disponível"
          text="Episódio encontrado em uma ou mais fontes."
        />
        <Feature
          icon={CircleAlert}
          title="Faltando"
          text="Já foi lançado oficialmente, mas não aparece em nenhuma fonte."
        />
        <Feature
          icon={Cloud}
          title="Futuro"
          text="Existe na base online, porém a data de estreia ainda não chegou."
        />
      </div>
    </div>
  );
}
function Feature({ icon: Icon, title, text }: any) {
  return (
    <div className="panel feature-card">
      <span>
        <Icon />
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function SourcesPage({
  sources,
  addSource,
  removeSource,
  catalog,
  updateSourceAfterSync,
}: any) {
  const [tab, setTab] = useState<"file" | "m3u-url" | "xtream">("file");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [include, setInclude] = useState(["vod", "series"]);
  const [selectedFileName, setSelectedFileName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  function makeSource(type: SourceConfig["type"]): SourceConfig {
    return {
      id: newId(),
      name: name.trim() || `Fonte ${sources.length + 1}`,
      type,
      url: url.trim() || undefined,
      username: username.trim() || undefined,
      enabled: true,
      createdAt: new Date().toISOString(),
    };
  }
  async function importFile() {
    const file = fileRef.current?.files?.[0];
    if (!file) return alert("Selecione um arquivo M3U");
    const source = makeSource("file");
    addSource(source);
    catalog.importFile(source, file);
  }
  async function importUrl() {
    if (!url.trim()) return alert("Informe a URL");
    const source = makeSource("m3u-url");
    addSource(source);
    catalog.importM3uUrl(source);
  }
  async function importXtream() {
    if (!url || !username || !password)
      return alert("Preencha servidor, usuário e senha");
    const source = makeSource("xtream");
    addSource(source);
    saveXtreamSession(source.id, password);
    catalog.importXtream(source, password, include);
  }

  return (
    <div className="page-stack">
      <section className="panel import-panel">
        <div className="tabs">
          <button
            className={tab === "file" ? "active" : ""}
            onClick={() => setTab("file")}
          >
            <FolderUp />
            Arquivo M3U
          </button>
          <button
            className={tab === "m3u-url" ? "active" : ""}
            onClick={() => setTab("m3u-url")}
          >
            <Network />
            URL M3U
          </button>
          <button
            className={tab === "xtream" ? "active" : ""}
            onClick={() => setTab("xtream")}
          >
            <Server />
            Xtream
          </button>
        </div>
        <div className="form-grid">
          <label>
            <span>Nome da fonte</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Club"
            />
          </label>
          {tab !== "file" && (
            <label className="span-2">
              <span>
                {tab === "xtream" ? "Servidor Xtream" : "URL da lista M3U"}
              </span>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={
                  tab === "xtream"
                    ? "https://servidor.exemplo:443"
                    : "https://exemplo/lista.m3u"
                }
              />
              <small>O link será salvo no localStorage deste navegador.</small>
            </label>
          )}
          {tab === "file" && (
            <label className="file-drop span-2">
              <UploadCloud />
              <b>Escolha uma lista .m3u</b>
              <span>
                O arquivo será processado localmente e só filmes/séries serão
                indexados.
              </span>
              <input
                ref={fileRef}
                type="file"
                accept=".m3u,.m3u8,text/plain"
                onChange={(e) =>
                  setSelectedFileName(e.currentTarget.files?.[0]?.name || "")
                }
              />
              <span className="file-picker-row">
                <strong>Selecionar arquivo</strong>
                <em>{selectedFileName || "Nenhum arquivo escolhido"}</em>
              </span>
            </label>
          )}
          {tab === "xtream" && (
            <>
              <label>
                <span>Usuário</span>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                />
              </label>
              <label>
                <span>Senha</span>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                />
                <small>
                  Fica apenas na sessão atual, nunca no localStorage.
                </small>
              </label>
              <div className="check-row span-2">
                {[
                  ["vod", "Filmes"],
                  ["series", "Séries"],
                ].map(([id, label]) => (
                  <label key={id}>
                    <input
                      type="checkbox"
                      checked={include.includes(id)}
                      onChange={() =>
                        setInclude((v) =>
                          v.includes(id)
                            ? v.filter((x) => x !== id)
                            : [...v, id],
                        )
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <button
          className="button primary import-button"
          onClick={
            tab === "file"
              ? importFile
              : tab === "m3u-url"
                ? importUrl
                : importXtream
          }
        >
          <Plus />
          Importar e indexar
        </button>
      </section>
      <section>
        <div className="section-heading">
          <div>
            <span className="eyebrow">FONTES SALVAS</span>
            <h2>{sources.length} servidor(es)</h2>
          </div>
        </div>
        <div className="source-grid">
          {sources.map((source: SourceConfig) => {
            const stat = catalog.stats.sources.find(
              (row: any) => row.sourceId === source.id,
            );
            return (
              <div className="source-card" key={source.id}>
                <span className="source-avatar large">
                  {source.name.slice(0, 2).toUpperCase()}
                </span>
                <div className="source-info">
                  <h3>{source.name}</h3>
                  <p>
                    {source.type}
                    {source.url ? ` • ${safeHost(source.url)}` : ""}
                  </p>
                  <small>
                    {stat?.count
                      ? `${formatNumber(stat.count)} filmes/séries indexados`
                      : source.itemCount
                        ? `${formatNumber(source.itemCount)} itens indexados`
                        : source.lastSyncAt
                          ? `Atualizado ${new Date(source.lastSyncAt).toLocaleString("pt-BR")}`
                          : "Ainda não sincronizado"}
                  </small>
                </div>
                <div className="source-actions">
                  {source.type === "m3u-url" && (
                    <button
                      className="icon-button"
                      title="Atualizar"
                      onClick={() => catalog.importM3uUrl(source)}
                    >
                      <RefreshCw />
                    </button>
                  )}
                  {source.type === "xtream" && (
                    <button
                      className="icon-button"
                      title="Atualizar"
                      onClick={() => {
                        const pass =
                          loadXtreamSession(source.id) ||
                          prompt("Senha Xtream para esta sessão:") ||
                          "";
                        if (pass) {
                          saveXtreamSession(source.id, pass);
                          catalog.importXtream(source, pass, ["vod", "series"]);
                        }
                      }}
                    >
                      <RefreshCw />
                    </button>
                  )}
                  <button
                    className="icon-button danger"
                    onClick={() => removeSource(source)}
                  >
                    <X />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function SettingsPage({ settings, setSettings, sources, clearCatalog }: any) {
  return (
    <div className="settings-layout">
      <section className="panel">
        <div className="panel-title">
          <Settings />
          <div>
            <h3>Aparência e busca</h3>
            <p>Preferências salvas no localStorage.</p>
          </div>
        </div>
        <div className="form-grid">
          <label>
            <span>Tema</span>
            <select
              value={settings.theme}
              onChange={(e) =>
                setSettings((s: AppSettings) => ({
                  ...s,
                  theme: e.target.value as AppSettings["theme"],
                }))
              }
            >
              <option value="dark">Escuro</option>
              <option value="light">Claro</option>
              <option value="system">Sistema</option>
            </select>
          </label>
          <label>
            <span>Máximo de resultados</span>
            <input
              type="number"
              min="20"
              max="500"
              value={settings.maxResults}
              onChange={(e) =>
                setSettings((s: AppSettings) => ({
                  ...s,
                  maxResults: Number(e.target.value),
                }))
              }
            />
          </label>
          <label className="span-2">
            <span>Apelidos de busca</span>
            <textarea
              rows={7}
              value={settings.aliases}
              onChange={(e) =>
                setSettings((s: AppSettings) => ({
                  ...s,
                  aliases: e.target.value,
                }))
              }
            />
          </label>
          <label className="switch-row span-2">
            <input
              type="checkbox"
              checked={settings.hideAdult}
              onChange={(e) =>
                setSettings((s: AppSettings) => ({
                  ...s,
                  hideAdult: e.target.checked,
                }))
              }
            />
            <span>
              <b>Ocultar conteúdo adulto</b>
              <small>Aplicado na pesquisa e nos totais visíveis.</small>
            </span>
          </label>
        </div>
      </section>
      <section className="panel">
        <div className="panel-title">
          <ShieldCheck />
          <div>
            <h3>Segurança</h3>
            <p>Auditoria aplicada nesta versão.</p>
          </div>
        </div>
        <ul className="security-list">
          <li>
            <Check />
            TMDb/OMDb ficam em secrets do Cloudflare, não no JavaScript.
          </li>
          <li>
            <Check />
            Links de fontes ficam no localStorage conforme solicitado.
          </li>
          <li>
            <Check />
            Senha Xtream fica somente no sessionStorage e é apagada ao fechar o
            navegador.
          </li>
          <li>
            <Check />
            URLs individuais dos streams não são persistidas.
          </li>
          <li>
            <Check />
            Proxy bloqueia localhost, IPs privados e pode usar allowlist de
            domínios.
          </li>
        </ul>
      </section>
      <section className="panel danger-zone">
        <div>
          <h3>Dados locais</h3>
          <p>Exporte as fontes antes de limpar.</p>
        </div>
        <div className="button-row">
          <button
            className="button soft"
            onClick={() => exportLocalConfig(sources, settings)}
          >
            <Download />
            Exportar configuração
          </button>
          <button
            className="button danger"
            onClick={() =>
              confirm("Apagar todo o catálogo IndexedDB?") && clearCatalog()
            }
          >
            <Database />
            Limpar catálogo
          </button>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ title, text }: any) {
  return (
    <div className="empty-state">
      <Radar />
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function compactDate(value?: string) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("pt-BR");
}
function formatRuntime(minutes?: number) {
  if (!minutes) return "";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours}h ${String(rest).padStart(2, "0")}min` : `${rest}min`;
}
function money(value?: number) {
  return value
    ? value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      })
    : "";
}
function Fact({ icon: Icon, label, value }: any) {
  if (!value) return null;
  return (
    <span>
      <Icon />
      {label ? `${label}: ` : ""}
      {value}
    </span>
  );
}

function CompactDetail({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <li>
      <span>
        <b>{label}:</b> {value}
      </span>
    </li>
  );
}

function DetailDrawer({
  detail,
  metadata,
  loading,
  selectedSeason,
  changeSeason,
  rows,
  isFavorite,
  onToggleFavorite,
  onClose,
}: any) {
  const title = metadata?.title || detail.title;
  const originalTitle =
    metadata?.originalTitle && metadata.originalTitle !== title
      ? metadata.originalTitle
      : "";
  const countries = metadata?.countries?.join(", ");
  const languages = metadata?.languages?.join(", ");
  const releaseLabel = metadata?.mediaType === "tv" ? "Estreia" : "Lançamento";
  const canFavorite = detail.kind === "movie" || detail.kind === "series";
  const certification = metadata?.certification?.trim();
  const certificationLabel = certification || "N/I";
  return (
    <div
      className="drawer-backdrop"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <aside className="detail-drawer">
        <button
          className="drawer-close"
          onClick={onClose}
          aria-label="Fechar detalhes"
        >
          <X />
        </button>
        <div className="detail-hero">
          {metadata?.backdropUrl && (
            <PosterImage
              className="backdrop"
              src={metadata.backdropUrl}
              alt=""
            />
          )}
          <div className="detail-gradient" />
          <div className="detail-heading">
            <PosterImage
              className="detail-poster"
              src={metadata?.posterUrl || detail.logo}
              alt={`Capa de ${title}`}
              fallback={
                <div className="detail-poster placeholder">
                  <Radar />
                </div>
              }
            />
            <div className="detail-copy">
              <div className="source-chips meta-chips">
                <span
                  className={
                    detail.kind === "series"
                      ? "chip-type series"
                      : detail.kind === "movie"
                        ? "chip-type movie"
                        : "chip-type channel"
                  }
                >
                  {detail.kind === "series"
                    ? "Série"
                    : detail.kind === "movie"
                      ? "Filme"
                      : "Canal"}
                </span>
                {detail.year && (
                  <span className="chip-year">{detail.year}</span>
                )}
                {canFavorite && (
                  <span
                    className={`chip-rating ${
                      certification
                        ? classificationClass(certification)
                        : "rating-unrated"
                    }`}
                    title={
                      certification
                        ? `Classificação indicativa: ${certification}`
                        : "Classificação indicativa não informada"
                    }
                  >
                    {certificationLabel}
                  </span>
                )}
              </div>
              <h2>{title}</h2>
              {originalTitle && (
                <strong className="original-title">{originalTitle}</strong>
              )}
              {metadata?.tagline && (
                <p className="tagline">{metadata.tagline}</p>
              )}
              <p>{metadata?.overview || "Sinopse ainda não encontrada."}</p>
              <div className="detail-facts">
                <Fact
                  icon={Activity}
                  value={formatRuntime(metadata?.runtime)}
                />
                <Fact
                  icon={Star}
                  label="TMDb"
                  value={metadata?.tmdbRating?.toFixed(1)}
                />
                <Fact icon={Star} label="IMDb" value={metadata?.imdbRating} />
                <Fact
                  icon={Info}
                  label={releaseLabel}
                  value={compactDate(metadata?.releaseDate)}
                />
              </div>
              {canFavorite && (
                <button
                  className={isFavorite ? "button favorite-action active" : "button favorite-action"}
                  onClick={onToggleFavorite}
                >
                  <Star fill={isFavorite ? "currentColor" : "none"} />
                  {isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="drawer-content">
          {loading && (
            <div className="loading-line">
              <RefreshCw className="spin" />
              Buscando metadados...
            </div>
          )}
          <section>
            <h3>Disponível em</h3>
            {detail.servers.length ? (
              <div className="server-presence">
                {detail.servers.map((s: string) => (
                  <span key={s}>
                    <Check />
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              <div className="catalog-only">
                <Info />
                <div>
                  <b>Não encontrado nos servidores cadastrados</b>
                  <p>
                    Este card está aberto como fonte de pesquisa online, com
                    dados públicos de catálogo.
                  </p>
                </div>
              </div>
            )}
          </section>
          {metadata?.genres?.length && (
            <section>
              <h3>Gêneros</h3>
              <div className="source-chips">
                {metadata.genres.map((g: string) => (
                  <span key={g}>{g}</span>
                ))}
              </div>
            </section>
          )}
          {detail.categories?.length && (
            <section>
              <h3>
                {detail.servers.length
                  ? "Categorias locais"
                  : "Origem da informação"}
              </h3>
              <div className="source-chips muted-chips">
                {detail.categories.map((g: string) => (
                  <span key={g}>{g}</span>
                ))}
              </div>
            </section>
          )}
          {metadata?.cast?.length && (
            <section>
              <h3>Elenco principal</h3>
              <div className="cast-rail">
                {metadata.cast.slice(0, 14).map((c: any) => (
                  <div key={c.name}>
                    {c.profileUrl ? (
                      <PosterImage src={c.profileUrl} alt="" />
                    ) : (
                      <span className="cast-placeholder" />
                    )}
                    <b>{c.name}</b>
                    <small>{c.character}</small>
                  </div>
                ))}
              </div>
            </section>
          )}
          {detail.kind === "series" && (
            <section>
              <div className="episode-head">
                <div>
                  <h3>Episódios</h3>
                  <p>
                    Comparação com a temporada oficial, incluindo sinopse e
                    disponibilidade local.
                  </p>
                </div>
                <select
                  value={selectedSeason}
                  onChange={(e) => changeSeason(Number(e.target.value))}
                >
                  {(metadata?.seasons || [])
                    .filter((s: any) => s.seasonNumber > 0)
                    .map((s: any) => (
                      <option key={s.seasonNumber} value={s.seasonNumber}>
                        Temporada {s.seasonNumber} ({s.episodeCount})
                      </option>
                    ))}
                </select>
              </div>
              <div className="episode-table">
                <div className="episode-row header">
                  <span>Ep.</span>
                  <span>Título e sinopse</span>
                  <span>Status</span>
                  <span>Servidores</span>
                </div>
                {rows.map((row: any) => (
                  <div className="episode-row" key={row.episode}>
                    <span className="episode-media">
                      <span className="ep-number">
                        E{String(row.episode).padStart(2, "0")}
                      </span>
                      {row.stillUrl && (
                        <PosterImage
                          className="episode-still"
                          src={row.stillUrl}
                          alt={`Imagem do episódio ${row.episode}`}
                        />
                      )}
                    </span>
                    <span className="episode-copy">
                      <b>{row.name}</b>
                      <small>
                        {[
                          compactDate(row.airDate),
                          formatRuntime(row.runtime),
                        ]
                          .filter(Boolean)
                          .join(" • ") || "Sem data"}
                      </small>
                      {row.overview && <p>{row.overview}</p>}
                    </span>
                    <span className={`status ${row.status}`}>
                      {row.status === "available"
                        ? "Disponível"
                        : row.status === "future"
                          ? "Futuro"
                          : "N/A"}
                    </span>
                    <span className="episode-sources">
                      {row.servers.length ? (
                        row.servers.map((s: string) => <em key={s}>{s}</em>)
                      ) : (
                        <small>Sem servidor</small>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {detail.originalNames?.length && (
            <section className="drawer-footer-section">
              <h3>Nome no arquivo/lista</h3>
              <ul className="compact-detail-list original-name-list">
                {detail.originalNames.map((name: string) => (
                  <li key={name}>
                    <code>{name}</code>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="drawer-footer-section">
            <h3>Detalhes</h3>
            <ul className="compact-detail-list">
              <CompactDetail
                label="Direção/criação"
                value={metadata?.director}
              />
              <CompactDetail label="País de origem" value={countries} />
              <CompactDetail label="Idioma" value={languages} />
              <CompactDetail label="Status" value={metadata?.status} />
              <CompactDetail
                label="Orçamento"
                value={money(metadata?.budget)}
              />
              <CompactDetail
                label="Receita"
                value={money(metadata?.revenue)}
              />
            </ul>
          </section>
        </div>
      </aside>
    </div>
  );
}

function buildEpisodeRows(
  detail: GroupDetail,
  official: OfficialEpisode[],
  season: number,
) {
  const local = detail.episodes.filter((ep) => ep.season === season);
  const localMap = new Map(local.map((ep) => [ep.episode, ep]));
  const localByTitle = local
    .map((ep) => ({ ...ep, titleKey: normalize(ep.title) }))
    .filter((ep) => ep.titleKey.length > 2);
  const today = new Date().toISOString().slice(0, 10);
  if (!official.length)
    return local.map((ep) => ({
      episode: ep.episode,
      name: ep.title,
      airDate: "",
      servers: ep.servers,
      status: "available",
    }));
  return official.map((ep) => {
    const titleKey = normalize(ep.name);
    const found =
      localMap.get(ep.episode) ||
      (titleKey.length > 2
        ? localByTitle.find(
            (localEp) =>
              localEp.titleKey.includes(titleKey) ||
              titleKey.includes(localEp.titleKey),
          )
        : undefined);
    const future = Boolean(ep.airDate && ep.airDate > today);
    return {
      episode: ep.episode,
      name: ep.name,
      overview: ep.overview,
      runtime: ep.runtime,
      stillUrl: ep.stillUrl,
      airDate: ep.airDate,
      servers: found?.servers || [],
      status: found ? "available" : future ? "future" : "missing",
    };
  });
}

function mergeSeasonEpisodes(
  detail: GroupDetail,
  remoteEpisodes: GroupDetail["episodes"],
  season: number,
): GroupDetail {
  const map = new Map(
    detail.episodes
      .filter((ep) => ep.season !== season)
      .map((ep) => [`${ep.season}:${ep.episode}`, { ...ep }]),
  );
  for (const ep of detail.episodes.filter((item) => item.season === season)) {
    map.set(`${ep.season}:${ep.episode}`, { ...ep, servers: [...ep.servers] });
  }
  for (const ep of remoteEpisodes) {
    const key = `${ep.season}:${ep.episode}`;
    const current = map.get(key);
    const servers = new Set([...(current?.servers || []), ...ep.servers]);
    map.set(key, {
      season: ep.season,
      episode: ep.episode,
      title: current?.title || ep.title,
      servers: [...servers].sort(),
    });
  }
  return {
    ...detail,
    episodes: [...map.values()].sort(
      (a, b) => a.season - b.season || a.episode - b.episode,
    ),
  };
}
